import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  gridFilteredSortedRowEntriesSelector,
  type useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useAtomValue, useAtomValueRawSync, useSetAtom } from "jotai";
import { usePanelResize } from "src/hooks/usePanelResize/usePanelResize";
import { useNodeTreeHashMapState } from "../PipelinePage/MetricsTabView/components/AssetsTree/AssetsTree.hooks";
import {
  resetHeaderFiltersAtom,
  useAssetFilters,
  useReadFilters,
} from "../PipelinePage/MetricsTabView/MetricsTable/filterModelAtom";
import { getEnhancedMetrics } from "../PipelinePage/MetricsTabView/MetricsTable/MetricsTable.utils";
import { useSelectedRow } from "../PipelinePage/MetricsTabView/MetricsTabView.atoms";
import {
  useFetchComparativeAssetsData,
  useFetchComparativeMetricsData,
} from "./ComparativeAnalysis.queries";
import {
  metricGroupsScoresAtom,
  similarityThresholdAtom,
} from "./ComparativeAnalysisPage.atoms";
import {
  checkGroupsFieldsIdentical,
  checkGroupsForMissingFields,
  getComparativeScoresFromMetrics,
} from "./ComparativeAnalysisPage.utils";
import {
  comparePitsFormSubmittedAtom,
  useFormStateAtoms,
} from "./components/ComparativeAnalysisForm/ComparativeAnalysisForm.atoms";
import { SEARCH_PARAM_NAMES } from "./components/ComparativeAnalysisForm/ComparativeAnalysisForm.constants";
import {
  getGroupsFromFormState,
  isPairedParamComplete,
  isTaskParamComplete,
} from "./components/ComparativeAnalysisForm/ComparativeAnalysisForm.utils";

export const useRequiredFieldsExistInURL = () => {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const env = searchParams.get(SEARCH_PARAM_NAMES.env);
    const pipeline = searchParams.get(SEARCH_PARAM_NAMES.pipeline);
    const pit = searchParams.get(SEARCH_PARAM_NAMES.pit);
    const task = searchParams.get(SEARCH_PARAM_NAMES.task);

    if (
      !isPairedParamComplete(env) ||
      !isPairedParamComplete(pipeline) ||
      !isPairedParamComplete(pit)
    ) {
      return false;
    }

    if (task != null && task !== "") {
      return isTaskParamComplete(task);
    }
    return true;
  }, [searchParams]);
};

export const useCompareFieldsFilled = () => {
  const formState = useFormStateAtoms();
  const groups = getGroupsFromFormState(formState);

  // Jotai 3 `useAtom` snapshots on first paint and only rerenders on `store.sub`.
  // The page's URL reset writes `false` in a useEffect registered before this
  // hook's subscribe; that notify is missed and a later `set(false)` is a no-op.
  // RawSync (useSyncExternalStore) re-reads and sees the tear.
  const submitted = useAtomValueRawSync(comparePitsFormSubmittedAtom);
  const setSubmitted = useSetAtom(comparePitsFormSubmittedAtom);

  const hasEmptyFields = useMemo(
    () => checkGroupsForMissingFields(groups),
    [groups]
  );

  const identicalGroupFields = useMemo(
    () => (!hasEmptyFields ? checkGroupsFieldsIdentical(groups) : false),
    [hasEmptyFields, groups]
  );

  // Complements URL-based reset on the page: clearing the form does not change the URL until
  // submit, so `useRequiredFieldsExistInURL` alone would leave `submitted` true with empty fields.
  useEffect(() => {
    if (hasEmptyFields) {
      setSubmitted(false);
    }
  }, [setSubmitted, hasEmptyFields]);

  return {
    submitted,
    setSubmitted,
    hasEmptyFields,
    identicalGroupFields,
  };
};

///////////////////////////////////////////////////////////////////////////////////////

export const useCoreComparativeAnalysis = () => {
  const { data: enhancedMetrics, isFetching: isFetchingMetrics } =
    useFetchComparativeMetricsData(getEnhancedMetrics);

  const assetsQuery = useFetchComparativeAssetsData();

  const [selectedMetricsRows, setSelectedMetricsRows] = useSelectedRow();
  const [apiRef, setApiRef] = useState<ReturnType<typeof useGridApiRef>>();

  const readFilter = useReadFilters();

  const setAssetReset = useSetAtom(resetHeaderFiltersAtom);
  const setMetricGroupsScores = useSetAtom(metricGroupsScoresAtom);
  const similarityThreshold = useAtomValue(similarityThresholdAtom);

  useEffect(() => {
    if (isFetchingMetrics || !apiRef?.current) {
      return;
    }

    const firstRowModel = gridFilteredSortedRowEntriesSelector(apiRef)
      ?.map((row) => row.model)
      .at(0);

    const firstRowId = firstRowModel?.uniqueKey;

    const selectedId = selectedMetricsRows.ids.values().next().value;

    // v8 semantics: visibleRowsLookup only stores `false` for hidden rows; visible rows are
    // absent. So "visible" means "not explicitly false" (matches MUI v8 migration guide).
    const isSelectedRowInRows =
      selectedId != null &&
      apiRef.current.state?.visibleRowsLookup[selectedId] !== false;

    if (!isSelectedRowInRows && firstRowId) {
      setSelectedMetricsRows({
        type: "include",
        ids: new Set([firstRowId]),
      });
    }
  }, [
    isFetchingMetrics,
    selectedMetricsRows,
    setSelectedMetricsRows,
    apiRef,
    readFilter,
  ]);

  // reset the metrics table filters on mount
  useEffect(() => {
    setAssetReset();
  }, [setAssetReset]);

  useEffect(() => {
    if (isFetchingMetrics) {
      setMetricGroupsScores({});
    } else if (enhancedMetrics) {
      setMetricGroupsScores(
        getComparativeScoresFromMetrics(enhancedMetrics, similarityThreshold)
      );
    }
  }, [
    enhancedMetrics,
    isFetchingMetrics,
    setMetricGroupsScores,
    similarityThreshold,
  ]);

  return {
    enhancedMetrics,
    isLoading: isFetchingMetrics || assetsQuery.isPending,
    assetsQuery,
    setApiRef,
  };
};

export const useSelectedTreeAssetName = () => {
  const filterByAssets = useAssetFilters().path.value;
  const [nodeTreeHashMap] = useNodeTreeHashMapState();
  return nodeTreeHashMap?.[filterByAssets]?.metadata?.asset_name;
};

export const useComparativeAnalysisPanelResize = () => {
  // Refs for resize functionality
  const assetsPanelRef = useRef<HTMLDivElement>(null);
  const contentPanelRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  // Panel resize configuration - 30/70 split
  const initialFlexValues = useMemo<[number, number]>(() => [3, 7], []);

  usePanelResize({
    handlerRef: resizeHandleRef,
    panelsRefs: [assetsPanelRef, contentPanelRef],
    range: {
      min: [2, 3],
    },
    initialFlexValues,
  });

  return {
    assetsPanelRef,
    contentPanelRef,
    resizeHandleRef,
  };
};
